import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet as WalletIcon, Hourglass, IndianRupee, ArrowDownToLine, Banknote } from "lucide-react";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr, fmtDate, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Payments() {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [upi, setUpi] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();

  const load = () => api.get("/user/wallet").then((r) => setData(r.data)).catch(() => setData({}));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.upi_id && !upi) setUpi(user.upi_id); }, [user]);

  const requestWithdrawal = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/user/withdrawals", { amount: parseFloat(amount), upi_id: upi });
      toast.success("Withdrawal request submitted");
      setAmount("");
      await load();
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" data-testid="payments-loading" /></div>;

  const rewardHistory = (data.transactions || []).filter((t) => t.type === "credit");

  return (
    <div data-testid="payments-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Payments</h1>
      <p className="mt-1 text-sm text-slate-600">Your rewards, balance and UPI withdrawals in one place.</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="payments-stats-grid">
        <StatCard label="Available Balance" value={inr(data.available_balance)} icon={WalletIcon} testid="user-wallet-balance-amount" accent="text-blue-600 bg-blue-50" />
        <StatCard label="Pending Rewards" value={inr(data.pending_rewards)} icon={Hourglass} testid="wallet-pending-rewards" accent="text-amber-600 bg-amber-50" />
        <StatCard label="Total Earned" value={inr(data.total_earned)} icon={IndianRupee} testid="wallet-total-earned" accent="text-emerald-600 bg-emerald-50" />
        <StatCard label="Total Withdrawn" value={inr(data.total_withdrawn)} icon={ArrowDownToLine} testid="wallet-total-withdrawn" accent="text-indigo-600 bg-indigo-50" />
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <h2 className="text-lg font-bold font-heading text-slate-900 p-5 sm:p-6 pb-3">Reward History</h2>
        {rewardHistory.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="reward-history-table">
              <thead>
                <tr className="border-y border-slate-100 bg-slate-50/60 text-left">
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Task</th>
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">Amount</th>
                  <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rewardHistory.map((t) => (
                  <tr key={t.id} data-testid={`reward-row-${t.id}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-slate-900">{t.description.replace("Reward — ", "")}</p>
                      <p className="text-xs text-slate-500">{t.task_code}</p>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">{fmtDate(t.date)}</td>
                    <td className="px-5 py-3.5 text-right font-semibold text-green-700 whitespace-nowrap">+{inr(t.amount)}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-5 sm:px-6 pb-6 text-sm text-slate-500" data-testid="rewards-empty">No rewards yet. Complete a task to earn your first reward.</p>
        )}
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-6" data-testid="withdrawal-section">
        <div className="flex items-center gap-2.5">
          <Banknote className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold font-heading text-slate-900">Withdraw</h2>
        </div>
        <p className="mt-1.5 text-xs text-slate-500">Minimum withdrawal: <strong data-testid="min-withdrawal-text">{inr(data.min_withdrawal)}</strong> · Paid to your UPI ID after admin approval.</p>
        {error && <div data-testid="withdrawal-error" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}
        <form onSubmit={requestWithdrawal} className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Amount (₹)</label>
            <input data-testid="withdrawal-amount-input" type="number" min="1" step="1" required value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={`Min ₹${data.min_withdrawal ?? 0}`} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">UPI ID</label>
            <input data-testid="withdrawal-upi-input" required value={upi} onChange={(e) => setUpi(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="yourname@upi" />
          </div>
          <button type="submit" data-testid="withdrawal-request-btn" disabled={loading}
            className="self-end w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Request Withdrawal
          </button>
        </form>

        <h3 className="mt-7 text-sm font-bold font-heading text-slate-900 uppercase tracking-wide">Withdrawal History</h3>
        {data.withdrawals?.length ? (
          <div className="mt-3 space-y-2.5" data-testid="withdrawal-history-list">
            {data.withdrawals.map((w) => (
              <div key={w.id} data-testid={`withdrawal-item-${w.withdrawal_code}`} className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3.5">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm">{inr(w.amount)} <span className="text-xs font-normal text-slate-500">· {w.withdrawal_code}</span></p>
                  <p className="text-xs text-slate-500">{w.upi_id} · {fmtDate(w.requested_at)}</p>
                  {w.admin_note && w.status === "rejected" && <p className="text-xs text-red-600 mt-1">Reason: {w.admin_note}</p>}
                </div>
                <StatusBadge status={w.status} />
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-500" data-testid="withdrawals-empty">No withdrawal requests yet.</p>
        )}
      </div>
    </div>
  );
}
